import { createContext, ReactNode, useMemo } from 'react';
import { mockRegistry } from 'mocks/registry';
import { useUserUUID } from 'utils/auth';
import { FeatureFlag } from '../constants';
import { useFeatureFlagsQuery } from '../query';
import {
  FeatureFlagsRequestData,
  FeaturesData,
  UnleashContextState
} from '../types';
import { getFeaturesDataFromList } from '../utils';

const allOff = (): FeaturesData =>
  Object.fromEntries(
    Object.values(FeatureFlag).map(name => [
      name,
      { enabled: false, variant: { name: 'disabled', enabled: false } }
    ])
  );

/**
 * The proxy's answer, with what the mock layer forces on laid over it; untouched when nothing is forced. Readers take
 * "features present" to mean every flag is in it, so while the answer is in flight the features stay null, and when
 * the proxy gave none (a local origin it refuses) the forced flags go over every flag off.
 */
const withMockFeatures = (
  features: FeaturesData | null,
  isLoading: boolean
): FeaturesData | null => {
  if (!Object.keys(mockRegistry.features).length || isLoading) return features;
  return { ...(features ?? allOff()), ...mockRegistry.features };
};

export const UnleashContext = createContext<UnleashContextState | undefined>(
  undefined
);

const UnleashProviderLogic = ({
  children,
  userId
}: {
  children: ReactNode;
  userId?: string;
}) => {
  const featureFlagsRequestData: FeatureFlagsRequestData = useMemo(
    () => ({
      features: Object.values(FeatureFlag),
      context: {
        userId
      }
    }),
    [userId]
  );

  const { isLoading, data } = useFeatureFlagsQuery(featureFlagsRequestData);

  const contextValue = useMemo(
    // `userId` travels with the features because it is what they were evaluated for. A query whose context changed
    // is a different query key, so while the answer for a new userId is in flight `isLoading` is true again and the
    // pair is never a fresh id next to a stale answer.
    () => ({
      // A dev build on mock data (src/mocks) forces the PJL flags on; every other build has nothing to force.
      features: withMockFeatures(
        getFeaturesDataFromList(data?.features),
        isLoading
      ),
      isLoading,
      userId
    }),
    [data?.features, isLoading, userId]
  );

  return (
    <UnleashContext.Provider value={contextValue}>
      {children}
    </UnleashContext.Provider>
  );
};

const AuthenticatedUnleashProvider = ({
  children
}: {
  children: ReactNode;
}) => {
  const userId = useUserUUID();

  return (
    <UnleashProviderLogic userId={userId}>{children}</UnleashProviderLogic>
  );
};

interface UnleashContextProviderProps {
  children: ReactNode;
  isPublic?: boolean;
}

export const UnleashContextProvider = ({
  children,
  isPublic = false
}: UnleashContextProviderProps) => {
  if (isPublic) {
    return (
      <UnleashProviderLogic userId={undefined}>{children}</UnleashProviderLogic>
    );
  }

  return (
    <AuthenticatedUnleashProvider>{children}</AuthenticatedUnleashProvider>
  );
};
